import type { ProcessListRow } from '@bliks-io/contracts';

// Department ids are ObjectIds, so this URL token cannot collide with one.
export const NO_DEPARTMENT = 'none';

export interface EditorDepartment {
  id: string;
  name: string;
  processes: ProcessListRow[];
}

export function groupEditableByDepartment(
  processes: ProcessListRow[],
  noDepartmentLabel: string,
  locale: string
): EditorDepartment[] {
  const byId = new Map<string, EditorDepartment>();
  for (const process of processes) {
    if (!process.capabilities.canUpdate) continue;
    const id = process.department?._id ?? NO_DEPARTMENT;
    const department = byId.get(id) ?? {
      id,
      name: process.department?.name ?? noDepartmentLabel,
      processes: []
    };
    department.processes.push(process);
    byId.set(id, department);
  }

  return Array.from(byId.values()).sort((a, b) => {
    if (a.id === NO_DEPARTMENT) return 1;
    if (b.id === NO_DEPARTMENT) return -1;
    return a.name.localeCompare(b.name, locale);
  });
}

export interface VisibleProcess {
  process: ProcessListRow;
  departmentName: string | undefined;
}

export function visibleProcesses(
  departments: EditorDepartment[],
  selected: EditorDepartment | undefined,
  query: string,
  locale: string
): VisibleProcess[] {
  const normalizedQuery = query.trim().toLocaleLowerCase(locale);
  return (selected ? [selected] : departments).flatMap((department) => {
    const matching =
      normalizedQuery === ''
        ? department.processes
        : department.processes.filter((process) =>
            // The department is shown on the row, so it has to be searchable too.
            `${process.name} ${department.name}`.toLocaleLowerCase(locale).includes(normalizedQuery)
          );
    return matching.map((process) => ({
      process,
      departmentName: selected ? undefined : department.name
    }));
  });
}
